import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageContainer from '../../layouts/PageContainer';
import PageHeader from '../../components/Nav/PageHeader';
import SummaryRow from '../../components/Config/SummaryRow';
import { useConfig } from '../../DataStore/ConfigContext';
import StarterPanel from '../../components/Dashboard/StarterPanel';

const Tile = ({ item }) => {
  if (!item) return null;
  const meta = item.meta || { label: '', value: '', badge: 'secondary' };

  return (
    <div className="col-sm-12 col-md-6 col-xl-3">
      <div className="card shadow-sm setting-tile h-100">
        <div className="card-body d-flex flex-column">
          <div className="d-flex align-items-center mb-3">
            <span
              className="rounded-circle d-inline-flex align-items-center justify-content-center mr-3"
              style={{ width: 40, height: 40, background: 'rgba(0,0,0,.06)' }}
            >
              <i className={`fas ${item.icon || ''}`} />
            </span>
            <div className="flex-grow-1">
              <h6 className="mb-0">{item.title}</h6>
              <small className="text-muted">{item.desc}</small>
            </div>
          </div>

          <div className="d-flex align-items-center mb-3">
            <span className={`badge badge-${meta.badge} mr-2`}>{meta.label}</span>
            <span className="text-muted small">{meta.value}</span>
          </div>

          <div className="mt-auto">
            <div className="btn-group btn-group-sm mb-2">
              {(item.actions || []).map((a) => (
                <Link key={`${item.key}-${a.label}`} to={a.to} className="btn btn-outline-secondary">
                  {a.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SkeletonSummary = () => (
  <div className="row mb-4">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="col-sm-6 col-md-3 mb-3">
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center">
              <div
                className="mr-3"
                style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(0,0,0,.08)' }}
              />
              <div className="w-100">
                <div style={{ height: 10, background: 'rgba(0,0,0,.08)' }} className="mb-2" />
                <div style={{ height: 10, width: '60%', background: 'rgba(0,0,0,.08)' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SkeletonTiles = () => (
  <div className="row">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="col-sm-12 col-md-6 col-xl-3 mb-3">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex align-items-center mb-3">
              <div
                className="mr-3"
                style={{ width: 40, height: 40, borderRadius: 20, background: 'rgba(0,0,0,.08)' }}
              />
              <div className="w-100">
                <div style={{ height: 12, background: 'rgba(0,0,0,.08)' }} className="mb-2" />
                <div style={{ height: 10, width: '70%', background: 'rgba(0,0,0,.08)' }} />
              </div>
            </div>
            <div style={{ height: 10, width: '50%', background: 'rgba(0,0,0,.08)' }} className="mb-2" />
            <div style={{ height: 10, width: '30%', background: 'rgba(0,0,0,.08)' }} />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SettingsPage = () => {
  const { getSettingsMenu, settingsMenu } = useConfig();

  useEffect(() => {
    getSettingsMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = useMemo(() => !settingsMenu || !settingsMenu.summary || !settingsMenu.tiles, [settingsMenu]);
  const summaryItems = settingsMenu?.summary || [];
  const tiles = settingsMenu?.tiles || [];

  return (
    <PageContainer>
      <PageHeader
        title="Settings"
        breadcrumb={
          <li className="breadcrumb-item active" aria-current="page">
            Settings
          </li>
        }
      />
      <div className="row">
        <StarterPanel greetingText='Hi'/>
        <div className="col-lg-8 mb-4"></div>
      </div>

      {/* Summary Row */}
      {isLoading ? <SkeletonSummary /> : <SummaryRow items={summaryItems} className="mb-4" />}

      {/* Tiles */}
      {isLoading ? (
        <SkeletonTiles />
      ) : tiles.length ? (
        <div className="row">
          {tiles.map((t) => (
            <Tile key={t.key} item={t} />
          ))}
        </div>
      ) : (
        <div className="text-center text-muted py-5">
          <i className="far fa-folder-open d-block mb-2" />
          No settings available yet.
        </div>
      )}
    </PageContainer>
  );
};

export default SettingsPage;
